import { useCallback, useEffect, useState } from 'react';

interface UseOtpCountdownPersistenceProps {
  sourceId: string;
  source: string;
  cooldownDuration?: number; // in seconds, default 30
}

const STORAGE_KEY_PREFIX = 'otp-countdown';

export const useOtpCountdownPersistence = ({
  sourceId,
  source,
  cooldownDuration = 30,
}: UseOtpCountdownPersistenceProps) => {
  const storageKey = `${STORAGE_KEY_PREFIX}-${source}-${sourceId}`;
  const [cooldown, setCooldown] = useState<number>(0);

  // Load countdown from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const { endTime } = JSON.parse(stored);
        const now = Date.now();
        const remaining = Math.max(0, Math.ceil((endTime - now) / 1000));

        if (remaining > 0) {
          setCooldown(remaining);
        } else {
          // Countdown expired, clean up
          localStorage.removeItem(storageKey);
        }
      }
    } catch (error) {
      console.error('Failed to load OTP countdown from localStorage:', error);
      localStorage.removeItem(storageKey);
    }
  }, [storageKey]);

  // Update countdown every second
  useEffect(() => {
    if (cooldown <= 0) return;

    const timer = setInterval(() => {
      setCooldown((prev) => {
        if (prev <= 1) {
          localStorage.removeItem(storageKey);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [cooldown, storageKey]);

  const activateCooldown = useCallback(() => {
    const endTime = Date.now() + cooldownDuration * 1000;
    try {
      localStorage.setItem(storageKey, JSON.stringify({ endTime }));
      setCooldown(cooldownDuration);
    } catch (error) {
      console.error('Failed to save OTP countdown to localStorage:', error);
    }
  }, [cooldownDuration, storageKey]);

  const resetCooldown = useCallback(() => {
    try {
      localStorage.removeItem(storageKey);
      setCooldown(0);
    } catch (error) {
      console.error('Failed to reset OTP countdown:', error);
    }
  }, [storageKey]);

  return {
    cooldown,
    activateCooldown,
    resetCooldown,
    isCooldownActive: cooldown > 0,
  };
};
